/**
 * Columnas AG Grid para el listado de reubicaciones (seguimiento).
 * Sin ordenación por columnas (mismo criterio que preparaciones).
 */
import { useMemo, type MouseEvent } from 'react';
import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faCartShopping, faEye } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { RelocationModel, RelocationStatus } from '../models/Relocation';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const statusBadgeClass = (status: RelocationStatus): string => {
  switch (status) {
    case 'draft':
      return 'text-badge-status-2 bg-card-border light:bg-badge-bg-status-2';
    case 'dispatched':
      return 'text-success bg-bg-success';
    case 'cancelled':
      return 'text-badge-status-4 bg-card-border light:bg-badge-bg-status-4';
    default:
      return 'text-subtext bg-card-border';
  }
};

// eslint-disable-next-line react-refresh/only-export-components
const StatusCell = ({ status, label }: { status: RelocationStatus; label: string }) => (
  <Badge variant="outline" className={`text-xs border-0 ${statusBadgeClass(status)}`}>
    {label}
  </Badge>
);

// eslint-disable-next-line react-refresh/only-export-components
const ActionsCell = ({ id, label }: { id: number; label: string }) => {
  const navigate = useNavigate();

  const path = `/relocations/${id}`;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
  };

  return (
    <div className="flex justify-center w-full">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleClick}
        aria-label={label}
        title={label}
        data-testid={`relocation-view-${id}`}
      >
        <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const useAgGridColumns = (selectedColumns: string[] = [], cartLabelsById: Record<number, string> = {}) => {
  const { t } = useTranslation('relocationsModule');

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      {
        field: 'location_order_id',
        headerName: t('table.location_order_id'),
        sortable: false,
        cellRenderer: (params: { data?: RelocationModel }) => (
          <span className="text-sm text-text">{params.data?.location_order_id ?? '-'}</span>
        ),
      },
      {
        field: 'cart_id',
        headerName: t('table.cart_id'),
        sortable: false,
        cellRenderer: (params: { data?: RelocationModel }) => {
          const cartId = params.data?.cart_id;
          if (!cartId) {
            return <span className="text-sm font-medium text-title">-</span>;
          }
          const label = cartLabelsById[cartId] || String(cartId);
          return (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-title">
              <FontAwesomeIcon icon={faCartShopping} className="w-3.5 h-3.5 text-icons" />
              <span>{label}</span>
            </span>
          );
        },
      },
      {
        field: 'status',
        headerName: t('table.status'),
        sortable: false,
        cellRenderer: (params: { data?: RelocationModel }) => {
          const row = params.data;
          if (!row) return null;
          return <StatusCell status={row.status} label={t(`filters.status_options.${row.status}`)} />;
        },
      },
      {
        field: 'dispatched_at',
        headerName: t('table.dispatched_at'),
        sortable: false,
        cellRenderer: (params: { data?: RelocationModel }) => (
          <span className="text-sm text-text">{formatDate(params.data?.dispatched_at)}</span>
        ),
      },
      {
        field: 'created_at',
        headerName: t('table.created_at'),
        sortable: false,
        cellRenderer: (params: { data?: RelocationModel }) => (
          <span className="text-sm text-text">{formatDate(params.data?.created_at)}</span>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        resizable: false,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        cellRenderer: (params: { data?: RelocationModel }) => {
          const row = params.data;
          if (!row) return null;
          return <ActionsCell id={row.id} label={t('actions.view')} />;
        },
      },
    ];

    return baseColumns;
  }, [cartLabelsById, t]);

  const filteredColumnDefs: ColDef[] = useMemo(() => {
    if (selectedColumns.length === 0) {
      return allColumnDefs;
    }
    // Siempre mostrar la columna de acciones aunque no esté en selectedColumns
    return allColumnDefs.filter((col) => {
      const fieldId = col.field || '';
      if (fieldId === 'actions') return true;
      return fieldId && selectedColumns.includes(fieldId);
    });
  }, [allColumnDefs, selectedColumns]);

  const tanStackColumns = useMemo<ColumnDef<RelocationModel>[]>(() => {
    // La columna "actions" se muestra siempre y no debe aparecer como opción en el selector
    return allColumnDefs
      .filter((col) => (col.field || '') !== 'actions')
      .map((col) => {
        const fieldId = col.field || '';
        return {
          id: fieldId,
          accessorKey: fieldId,
          header: col.headerName || fieldId,
        } as ColumnDef<RelocationModel>;
      });
  }, [allColumnDefs]);

  return {
    columnDefs: filteredColumnDefs,
    tanStackColumns,
  };
};
